import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildRoomPackageOptionsViewOrder, filterPackageOptionsBySkiPackageType } from 'lib/offer/offerPageUtil'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOption, getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import FlashHotelRateOptionVariant from '../FlashHotelRateOptionVariant'
import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import FlashMobileStickyHeaderFilters from '../FlashMobileStickyHeaderFilters'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Sheet from 'components/utils/Sheet'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const CarouselContainer = styled.div`
  padding: ${rem(12)};
`

const CarouselItemContainer = styled(Sheet)`
  min-width: 75vw;
  width: 100%;
`

interface Props {
  room: App.PackageRoomType
  offer: App.Offer
  airports?: Array<App.Airport>
  packageOptions: Array<App.OfferPackageOptionView>
  onClose?: () => void
  onBack?: () => void
  cheapestSelectedOption?: App.OfferPackageOptionView
}

function FlashHotelPackageSelectionModalWithCarousel(props: Props) {
  const {
    room,
    offer,
    airports,
    packageOptions: basePackageOptions,
    cheapestSelectedOption,
    onClose,
    onBack,
  } = props

  // TEMP: Remove this logic when "006RF00000IRdvlYAD" becomes unavailable
  const { packageOptions } = useMemo(() => {
    return filterPackageOptionsBySkiPackageType(basePackageOptions, cheapestSelectedOption)
  }, [basePackageOptions, cheapestSelectedOption])

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  return (
    <>
      <ModalHeader
        title="Select a package"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <FlashMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="none">
          {packageViews.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', packageViews.length)} available</BodyText>
            </OptionsContainer>
          )}

          <CarouselContainer>
            <CardCarousel
              snap="start"
              width={packageViews.length === 1 ? '100%' : 'auto'}
              gap={12}
            >
              {packageViews.map((view, index) => (
                <CarouselItemContainer
                  key={view.package.uniqueKey}
                  border="neutral-six"
                >
                  <FlashHotelRateOptionVariant
                    offer={offer}
                    optionNumber={index + 1}
                    packageView={view}
                    flightDestination={destinationAirport}
                    showOptionLabel={packageViews.length > 1}
                  />
                </CarouselItemContainer>
              ))}
            </CardCarousel>
          </CarouselContainer>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  offer: state.offer,
  rooms: state.rooms,
  airports: appState.geo.airports,
  packageOptions: getDurationPackageOptions(state, appState),
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
}), FlashHotelPackageSelectionModalWithCarousel)
